import { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";

import CustomDropdown from "../../Components/customs/dropdowns/CustomDropdown";
import { CalendarClock, X } from "lucide-react";

const ClassesModal = ({ currentClass, onSubmit, campuses }) => {
  // Accepting currentClass as a prop
  const [formData, setFormData] = useState({
    className: "",
    creditHours: "",
    startedSession: "",
    endSession: "",
    sections: [],
    classType: "Online",
    classSubjects: [],
    classCurriculum: "IB", // Added classCurriculum field with default value
  });

  const [mainInputs, setMainInputs] = useState([]);
  const [subjectChips, setSubjectChips] = useState([]);
  const [sectionChips, setSectionChips] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState("");

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const [specialEvents, setSpecialEvents] = useState([
    { name: "", date: "", from: "", to: "" }
  ]);

  // Effect to set default values for the form when editing
  useEffect(() => {
    if (currentClass) {
      const sections = currentClass.sections || [];
      setFormData({
        className: currentClass.className || "",
        creditHours: currentClass.creditHours || 0,
        startedSession: currentClass.startedSession
          ? new Date(currentClass.startedSession).toISOString().split("T")[0]
          : "",
        endSession: currentClass.endSession
          ? new Date(currentClass.endSession).toISOString().split("T")[0]
          : "",
        sections: sections,
        classType: currentClass.classType || "Online",
        classSubjects: currentClass.subjects || [],
        classCurriculum: currentClass.classCurriculum || "IB", // Added classCurriculum handling
      });
    
      if (currentClass.campusId) {
        setSelectedCampus(currentClass.campusId);
      }
      
      // Map the subjects with proper day structures
      if (currentClass.subjects && Array.isArray(currentClass.subjects)) {
        setMainInputs(
          currentClass.subjects.map((subject) => ({
            subject: subject.name,
            days: subject.days.map((day) => ({
              day: day.day.toLowerCase().charAt(0).toUpperCase() + day.day.toLowerCase().slice(1), // Normalize day name
              section: day.section,
              start_time: day.startTime,
              end_time: day.endTime,
            })),
            options: subject.options || [],
          }))
        );
      }

      setSectionChips(sections);
      
      if (currentClass.events && Array.isArray(currentClass.events)) {
        setSpecialEvents(currentClass.events.length > 0 ? 
          currentClass.events.map(event => ({
            name: event.name,
            date: event.date ? new Date(event.date).toISOString().split("T")[0] : "",
            from: event.from || event.startTime || "", // Handle both formats
            to: event.to || event.endTime || "",
          })) : 
          [{ name: "", date: "", from: "", to: "" }]
        );
      }
    }
  }, [currentClass]);

  useEffect(() => {
    if (!currentClass) {
      setFormData({
        className: "",
        creditHours: "",
        startedSession: "",
        endSession: "",
        sections: [],
        classType: "Online",
        classSubjects: [],
        classCurriculum: "IB", // Added classCurriculum reset
      });
      setMainInputs([]);
      setSectionChips([]);
      setSpecialEvents([{ name: "", date: "", from: "", to: "" }]);
    }
  }, [currentClass]);

  const removeChip = (chipToRemove, chipType) => {
    if (chipType === "subject") {
      setSubjectChips(subjectChips.filter((chip) => chip !== chipToRemove));
    }
    if (chipType === "sections") {
      setSectionChips(sectionChips.filter((chip) => chip !== chipToRemove));
    }
  };

  const removeLastChip = (chipType) => {
    if (chipType === "subject") setSubjectChips(subjectChips.slice(0, -1));
    if (chipType === "sections") setSectionChips(sectionChips.slice(0, -1));
  };

  const addChip = (chip, chipType) => {
    if (chipType === "subject") setSubjectChips([...subjectChips, chip]);
    if (chipType === "sections") setSectionChips([...sectionChips, chip]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  const handleAddSpecialEvent = () => {
    setSpecialEvents([...specialEvents, { name: "", date: "", from: "", to: "" }]);
  };

  const handleRemoveSpecialEvent = (index) => {
    const updatedEvents = specialEvents.filter((_, i) => i !== index);
    setSpecialEvents(updatedEvents.length > 0 ? updatedEvents : [{ name: "", date: "", from: "", to: "" }]);
  };

  const handleSpecialEventChange = (index, field, value) => {
    const updatedEvents = [...specialEvents];
    updatedEvents[index][field] = value;
    setSpecialEvents(updatedEvents);
  };

  const handleSubmit = async () => {
    const { className, classType, creditHours, startedSession, endSession, classCurriculum } = formData;

    // Validate that we have at least one subject
    if (mainInputs.length === 0) {
      toast.error("At least one subject is required");
      return;
    }

    // Format subjects to match API expectations
    const formattedSubjects = mainInputs.map((input) => ({
      name: input.subject,
      days: input.days.map((day) => ({
        day: day.day.toLowerCase(), // Make sure day is lowercase for consistency with API
        section: day.section,
        startTime: day.start_time, // Map to startTime for API
        endTime: day.end_time,    // Map to endTime for API
      })),
    }));

    const payload = {
      className,
      campusId: selectedCampus,
      creditHours: creditHours ? parseInt(creditHours, 10) : null,
      startedSession: new Date(startedSession).toISOString(),
      endSession: new Date(endSession).toISOString(),
      sections: sectionChips,
      classType,
      classCurriculum, // Added classCurriculum to payload
      // Use both keys to ensure compatibility
      Subjects: formattedSubjects,  // Capital S for API requirement
      subjects: formattedSubjects,  // lowercase for compatibility with existing code
      events: specialEvents
      .map(event => 
        event.name && event.date && event.from && event.to
        ? {
          name: event.name,
          date: new Date(event.date).toISOString().split('T')[0],
          startTime: event.from?.length === 5 ? `${event.from}:00` : event.from, // Use startTime to match API
          endTime: event.to?.length === 5 ? `${event.to}:00` : event.to,       // Use endTime to match API
          }
        : null
      )
      .filter(Boolean)
    };

    try {
      // Make sure we have required fields
      if (!payload.className) {
        toast.error("Class name is required");
        return;
      }
      
      if (!payload.campusId) {
        toast.error("Campus selection is required");
        return;
      }

      if (!payload.startedSession || !payload.endSession) {
        toast.error("Start and end session dates are required");
        return;
      }

      if (payload.sections.length === 0) {
        toast.error("At least one section is required");
        return;
      }

      await onSubmit(payload);
      // Reset form after submission
      setFormData({
        className: "",
        creditHours: "",
        startedSession: "",
        endSession: "",
        sections: [],
        classType: "Online",
        classSubjects: [],
        classCurriculum: "IB", // Added classCurriculum reset
      });
      setSelectedCampus("");
      setMainInputs([]);
      setSectionChips([]);
      setSubjectChips([]);
      setSpecialEvents([{ name: "", date: "", from: "", to: "" }]);
    } catch (err) {
      console.error("Submission error:", err);
      toast.error("Something went wrong, please try again.");
    }
  };

  const handleAddInputs = () => {
    const newInputs = {
      subject: "",
      days: [{ day: "Monday", section: sectionChips[0] || "", start_time: "", end_time: "" }],
      options: [],
    };
    setMainInputs([...mainInputs, newInputs]);
  };

  const addMoreDays = (index) => {
    const newDays = { 
      day: "Monday", 
      section: mainInputs[index].days[0]?.section || sectionChips[0] || "", 
      start_time: "", 
      end_time: "" 
    };
    const newItems = [...mainInputs];
    newItems[index].days.push(newDays);
    setMainInputs(newItems);
  };

  const handleChangeMainInputs = (value, name, index, dayIndex = null) => {
    const oldInputs = [...mainInputs];
    if (dayIndex === null) {
      oldInputs[index][name] = value; // Update subject name or options
    } else {
      if (name === "start_time" || name === "end_time") {
        // Enforce time format (hh:mm:ss)
        const formattedValue = value.length === 5 ? `${value}:00` : value;
        oldInputs[index].days[dayIndex][name] = formattedValue;
      } else {
        oldInputs[index].days[dayIndex][name] = value;
      }
    }
    setMainInputs(oldInputs);
  };

  const handleRemoveSubject = (index) => {
    const newSubjects = [...mainInputs];
    newSubjects.splice(index, 1);
    setMainInputs(newSubjects);
  };

  const handleRemoveDay = (subjectIndex, dayIndex) => {
    const newSubjects = [...mainInputs];
    newSubjects[subjectIndex].days.splice(dayIndex, 1);
    setMainInputs(newSubjects);
  };

  return (
    <>
      <Toaster />
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 d-flex justify-content-center py-2 fw-bold fs-6">
            Class Information
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="class">Class/Standard</label>
              <input
                type="text"
                className="form-control"
                placeholder="Class"
                name="className"
                onChange={handleInputChange}
                value={formData.className}
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="class">Class type</label>
              <select
                className="form-select"
                aria-label="Default select example"
                name="classType"
                onChange={handleInputChange}
                value={formData.classType}
              >
                <option>Online</option>
                <option>Physical</option>
                <option>Both</option>
              </select>
            </div>
          </div>
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label htmlFor="classCurriculum">Class Curriculum</label>
              <select
                className="form-select"
                aria-label="Class Curriculum select"
                name="classCurriculum"
                onChange={handleInputChange}
                value={formData.classCurriculum}
              >
                <option value="IB">IB</option>
                <option value="Cambridge">Cambridge</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label htmlFor="class">Sections</label>
              <CustomDropdown
                placeholder="to add sections"
                chips={sectionChips}
                addChip={addChip}
                removeLastChip={removeLastChip}
                setChips={setSectionChips}
                removeChip={removeChip}
                chipType="sections"
              />
              {sectionChips.length === 0 && (
                <small className="text-danger">At least one section is required</small>
              )}
            </div>
          </div>
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label htmlFor="class">Credit Hours</label>
              <input
                type="number"
                className="form-control"
                placeholder="Credit hour"
                name="creditHours"
                onChange={handleInputChange}
                value={formData.creditHours}
              />
            </div>
          </div>
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label htmlFor="" className="text-capitalize">
                Start Session
              </label>
              <input
                type="date"
                name="startedSession"
                className="form-control"
                onChange={handleInputChange}
                value={formData.startedSession}
                required
              />
            </div>
          </div>
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label htmlFor="" className="text-capitalize">
                End Session
              </label>
              <input
                type="date"
                name="endSession"
                className="form-control"
                value={formData.endSession}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="my-2">
            <label>Select Campus</label>
            <select
              className="form-select"
              value={selectedCampus}
              onChange={handleCampusChange}
              required
            >
              <option value="">Select Campus</option>
              {Array.isArray(campuses) &&
                campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name} - {campus.location}
                  </option>
                ))}
            </select>
            {!selectedCampus && (
              <small className="text-danger">Campus selection is required</small>
            )}
          </div>
          <div className="col-md-12 mt-3">
          <div className="form-group mt-4">
            <div className="d-flex justify-content-between">
              <label>Special Events</label>
              <button
                type="button"
                className="btn btn-secondary mb-3"
                onClick={handleAddSpecialEvent}
              >
                + Add Special Event
              </button>
            </div>
            
            {specialEvents.map((event, index) => (
              <div key={index} className="mb-3 position-relative">
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Event name"
                    value={event.name}
                    onChange={(e) => 
                      handleSpecialEventChange(index, 'name', e.target.value)
                    }
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={event.date}
                    onChange={(e) => 
                      handleSpecialEventChange(index, 'date', e.target.value)
                    }
                  />
                  <input
                    type="time"
                    className="form-control"
                    value={event.from}
                    onChange={(e) => 
                      handleSpecialEventChange(index, 'from', e.target.value)
                    }
                  />
                  <span>to</span>
                  <input
                    type="time"
                    className="form-control"
                    value={event.to}
                    onChange={(e) => 
                      handleSpecialEventChange(index, 'to', e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleRemoveSpecialEvent(index)}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
          <div className="col-md-12 mt-3">
            <div className="form-group mt-4">
              <div className="d-flex justify-content-between">
                <label>Add subject per week</label>
                <button
                  type="button"
                  className="btn btn-secondary mb-3"
                  onClick={handleAddInputs}
                >
                  Add Subjects
                </button>
              </div>
              {mainInputs.length === 0 && (
                <div className="alert alert-warning">
                  At least one subject is required
                </div>
              )}
              <div className="d-flex flex-column gap-2">
                {mainInputs?.map((inputs, index) => (
                  <div key={index}>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="subject name"
                        name="subject"
                        value={inputs.subject}
                        onChange={(e) =>
                          handleChangeMainInputs(
                            e.target.value,
                            e.target.name,
                            index
                          )
                        }
                        required
                      />
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveSubject(index)}
                      >
                        &#x2715;
                      </button>
                      <CalendarClock
                        height={40}
                        width={40}
                        onClick={() => addMoreDays(index)}
                        className="cursor-pointer"
                      />
                    </div>

                    {inputs.days?.map((day, dayIndex) => (
                      <div
                        className="d-flex align-items-center gap-4 px-4 mt-3"
                        key={dayIndex}
                      >
                        <select
                          className="form-select"
                          name="day"
                          value={day.day}
                          onChange={(e) =>
                            handleChangeMainInputs(
                              e.target.value,
                              e.target.name,
                              index,
                              dayIndex
                            )
                          }
                        >
                          {days?.map((day, i) => (
                            <option value={day} key={i}>
                              {day}
                            </option>
                          ))}
                        </select>

                        <select
                          className="form-select"
                          name="section"
                          value={day.section || ""}
                          onChange={(e) =>
                            handleChangeMainInputs(
                              e.target.value,
                              e.target.name,
                              index,
                              dayIndex
                            )
                          }
                          required
                        >
                          <option value="">Select Section</option>
                          {sectionChips?.map((section, i) => (
                            <option value={section} key={i}>
                              {section}
                            </option>
                          ))}
                        </select>

                        <input
                          type="time"
                          className="form-control"
                          name="start_time"
                          value={day.start_time}
                          onChange={(e) =>
                            handleChangeMainInputs(
                              e.target.value,
                              e.target.name,
                              index,
                              dayIndex
                            )
                          }
                          required
                        />
                        <label>to</label>
                        <input
                          type="time"
                          className="form-control"
                          name="end_time"
                          value={day.end_time}
                          onChange={(e) =>
                            handleChangeMainInputs(
                              e.target.value,
                              e.target.name,
                              index,
                              dayIndex
                            )
                          }
                          required
                        />
                        <button
                          className="btn btn-danger btn-sm ms-2"
                          onClick={() => handleRemoveDay(index, dayIndex)}
                        >
                          &#x2715;
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 py-4 flex justify-end">
            <div className="form-group mt-4">
              <button
                className="btn bg-main text-white btn-primary"
                onClick={() => handleSubmit()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClassesModal;